import React, { useEffect, useMemo, useState, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DatabaseIcon, LinkIcon, PlusIcon, XIcon, BarChart3Icon, LineChartIcon, PieChartIcon, TableIcon, BookmarkIcon } from 'lucide-react';
import { datasets, getDataset } from '../data/datasets';
import { joinsForDataset, getJoin, getMergedFields } from '../data/joins';
import { ReportConfig, ReportFilter, ChartType, AggregateFn } from '../data/reports';
import { runReport } from '../utils/reportEngine';
import { ReportTable } from '../components/reports/ReportTable';
import { ReportChart } from '../components/reports/ReportChart';
import { ExportButton } from '../components/reports/ExportButton';
import { SaveTemplateDialog } from '../components/builder/SaveTemplateDialog';
import { useApp } from '../context/AppContext';
const chartOptions: {
  value: ChartType;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
}[] = [{
  value: 'table',
  label: 'Table',
  icon: TableIcon
}, {
  value: 'bar',
  label: 'Bar',
  icon: BarChart3Icon
}, {
  value: 'line',
  label: 'Line',
  icon: LineChartIcon
}, {
  value: 'pie',
  label: 'Pie',
  icon: PieChartIcon
}];
function defaultConfig(datasetId: string): ReportConfig {
  const ds = getDataset(datasetId)!;
  return {
    datasetId,
    joinId: null,
    columns: ds.fields.slice(0, 5).map(f => f.key),
    filters: [],
    groupBy: null,
    metricField: null,
    metricFn: 'count',
    chartType: 'table'
  };
}
const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-navy-400';
const selectCls = 'mt-1.5 h-9 w-full rounded-lg border border-navy-100 bg-white px-2.5 text-sm text-navy-900 focus:border-navy-300 focus:outline-none focus:ring-2 focus:ring-navy-900/10';
export function ReportBuilder() {
  const {
    templates,
    saveTemplate
  } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const templateId = searchParams.get('template');
  const loadedTemplate = templates.find(t => t.id === templateId);
  const [config, setConfig] = useState<ReportConfig>(() => loadedTemplate?.config ?? defaultConfig('appointments'));
  const [saveOpen, setSaveOpen] = useState(false);
  useEffect(() => {
    if (loadedTemplate) setConfig(loadedTemplate.config);
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps
  const dataset = getDataset(config.datasetId)!;
  const availableJoins = joinsForDataset(config.datasetId);
  const activeJoin = config.joinId ? getJoin(config.joinId) : undefined;
  const fields = useMemo(() => getMergedFields(config.datasetId, config.joinId), [config.datasetId, config.joinId]);
  const numericFields = fields.filter(f => f.type === 'number');
  const result = useMemo(() => runReport(config), [config]);
  const update = (patch: Partial<ReportConfig>) => setConfig(prev => ({
    ...prev,
    ...patch
  }));
  const handleDatasetChange = (id: string) => {
    setConfig(defaultConfig(id));
    if (templateId) setSearchParams({});
  };
  const handleJoinChange = (joinId: string) => {
    const prefix = activeJoin ? `${activeJoin.joinedId}.` : null;
    setConfig(prev => ({
      ...prev,
      joinId: joinId || null,
      // Drop selections that referenced the previous join's fields
      columns: prefix ? prev.columns.filter(c => !c.startsWith(prefix)) : prev.columns,
      filters: prefix ? prev.filters.filter(f => !f.field.startsWith(prefix)) : prev.filters,
      groupBy: prefix && prev.groupBy?.startsWith(prefix) ? null : prev.groupBy,
      metricField: prefix && prev.metricField?.startsWith(prefix) ? null : prev.metricField
    }));
  };
  const toggleColumn = (key: string) => {
    update({
      columns: config.columns.includes(key) ? config.columns.filter(c => c !== key) : [...config.columns, key]
    });
  };
  const addFilter = () => update({
    filters: [...config.filters, {
      field: fields[0].key,
      operator: 'contains',
      value: ''
    }]
  });
  const updateFilter = (index: number, patch: Partial<ReportFilter>) => update({
    filters: config.filters.map((f, i) => i === index ? {
      ...f,
      ...patch
    } : f)
  });
  const removeFilter = (index: number) => update({
    filters: config.filters.filter((_, i) => i !== index)
  });
  const reportName = loadedTemplate?.name ?? 'Custom Report';
  const showChart = config.chartType !== 'table' && result?.grouped;
  return <div className="flex h-full flex-col gap-4 p-4 sm:p-5 lg:flex-row" data-id="element-245">
      {/* Config panel */}
      <div className="card-float w-full shrink-0 overflow-y-auto rounded-2xl bg-white p-5 thin-scroll lg:h-full lg:w-80" data-id="element-246">
        <div className="mb-5" data-id="element-247">
          <h2 className="text-sm font-semibold text-navy-900" data-id="element-248">
            {loadedTemplate ? `Editing: ${loadedTemplate.name}` : 'New Custom Report'}
          </h2>
          <p className="mt-0.5 text-xs text-navy-400" data-id="element-249">
            Configure your report — the preview updates live.
          </p>
        </div>

        <div className="space-y-6" data-id="element-250">
          <div data-id="element-251">
            <label htmlFor="dataset" className={labelCls} data-id="element-252">
              <span className="inline-flex items-center gap-1.5" data-id="element-253">
                <DatabaseIcon className="h-3 w-3" data-id="element-254" /> Dataset
              </span>
            </label>
            <select id="dataset" value={config.datasetId} onChange={e => handleDatasetChange(e.target.value)} className={selectCls} data-id="element-255">
              {datasets.map(d => <option key={d.id} value={d.id} data-id="element-256">
                  {d.name}
                </option>)}
            </select>
            <p className="mt-1.5 text-[11px] leading-relaxed text-navy-300" data-id="element-257">
              {dataset.description}
            </p>
          </div>

          {availableJoins.length > 0 && <div data-id="element-258">
              <label htmlFor="join" className={labelCls} data-id="element-259">
                <span className="inline-flex items-center gap-1.5" data-id="element-260">
                  <LinkIcon className="h-3 w-3" data-id="element-261" /> Join Dataset
                </span>
              </label>
              <select id="join" value={config.joinId ?? ''} onChange={e => handleJoinChange(e.target.value)} className={selectCls} data-id="element-262">
                <option value="" data-id="element-263">None (single dataset)</option>
                {availableJoins.map(j => <option key={j.id} value={j.id} data-id="element-264">
                    + {j.name}
                  </option>)}
              </select>
              {activeJoin ? <p className="mt-1.5 text-[11px] leading-relaxed text-navy-300" data-id="element-265">
                  {activeJoin.description} Rows without a match keep blank
                  joined fields; multiple matches repeat the base row.
                </p> : <p className="mt-1.5 text-[11px] leading-relaxed text-navy-300" data-id="element-266">
                  Combine this dataset with a related one using a curated
                  relationship.
                </p>}
            </div>}

          <fieldset data-id="element-267">
            <legend className={labelCls} data-id="element-268">Columns</legend>
            <div className="mt-2 space-y-0.5" data-id="element-269">
              {fields.map(f => <label key={f.key} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-navy-800 transition-colors hover:bg-navy-50" data-id="element-270">
                  <input type="checkbox" checked={config.columns.includes(f.key)} onChange={() => toggleColumn(f.key)} className="h-4 w-4 rounded border-navy-200 accent-[#102a43] focus:ring-navy-300" data-id="element-271" />
                  <span className="flex-1" data-id="element-272">{f.label}</span>
                  <span className="text-[10px] uppercase text-navy-300" data-id="element-273">
                    {f.type}
                  </span>
                </label>)}
            </div>
          </fieldset>

          <div data-id="element-274">
            <div className="flex items-center justify-between" data-id="element-275">
              <span className={labelCls} data-id="element-276">Filters</span>
              <button type="button" onClick={addFilter} className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-colors hover:text-accent-800" data-id="element-277">
                <PlusIcon className="h-3.5 w-3.5" data-id="element-278" /> Add
              </button>
            </div>
            {config.filters.length === 0 ? <p className="mt-2 text-xs text-navy-300" data-id="element-279">No filters applied.</p> : <div className="mt-2 space-y-2" data-id="element-280">
                {config.filters.map((f, i) => <div key={i} className="rounded-xl bg-navy-50/60 p-2.5" data-id="element-281">
                    <div className="flex items-center gap-2" data-id="element-282">
                      <select value={f.field} onChange={e => updateFilter(i, {
                  field: e.target.value
                })} aria-label="Filter field" className="h-8 flex-1 rounded-lg border border-navy-100 bg-white px-2 text-xs text-navy-900 focus:border-navy-300 focus:outline-none" data-id="element-283">
                        {fields.map(df => <option key={df.key} value={df.key} data-id="element-284">
                            {df.label}
                          </option>)}
                      </select>
                      <select value={f.operator} onChange={e => updateFilter(i, {
                  operator: e.target.value as ReportFilter['operator']
                })} aria-label="Filter operator" className="h-8 w-24 rounded-lg border border-navy-100 bg-white px-2 text-xs text-navy-900 focus:border-navy-300 focus:outline-none" data-id="element-285">
                        <option value="contains" data-id="element-286">contains</option>
                        <option value="equals" data-id="element-287">equals</option>
                      </select>
                      <button type="button" onClick={() => removeFilter(i)} aria-label="Remove filter" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-navy-300 transition-colors hover:bg-white hover:text-rose-600" data-id="element-288">
                        <XIcon className="h-3.5 w-3.5" data-id="element-289" />
                      </button>
                    </div>
                    <input type="text" value={f.value} onChange={e => updateFilter(i, {
                value: e.target.value
              })} placeholder="Value…" aria-label="Filter value" className="mt-2 h-8 w-full rounded-lg border border-navy-100 bg-white px-2 text-xs text-navy-900 placeholder:text-navy-300 focus:border-navy-300 focus:outline-none" data-id="element-290" />
                  </div>)}
              </div>}
          </div>

          <div data-id="element-291">
            <label htmlFor="groupBy" className={labelCls} data-id="element-292">
              Group By
            </label>
            <select id="groupBy" value={config.groupBy ?? ''} onChange={e => update({
            groupBy: e.target.value || null
          })} className={selectCls} data-id="element-293">
              <option value="" data-id="element-294">None (detail rows)</option>
              {fields.filter(f => f.type === 'string').map(f => <option key={f.key} value={f.key} data-id="element-295">
                    {f.label}
                  </option>)}
            </select>
          </div>

          {config.groupBy && <div className="grid grid-cols-2 gap-2" data-id="element-296">
              <div data-id="element-297">
                <label htmlFor="metricFn" className={labelCls} data-id="element-298">
                  Measure
                </label>
                <select id="metricFn" value={config.metricFn} onChange={e => {
              const fn = e.target.value as AggregateFn;
              update({
                metricFn: fn,
                metricField: fn === 'count' ? null : config.metricField ?? numericFields[0]?.key ?? null
              });
            }} className={selectCls} data-id="element-299">
                  <option value="count" data-id="element-300">Count</option>
                  <option value="sum" data-id="element-301">Sum</option>
                  <option value="avg" data-id="element-302">Average</option>
                </select>
              </div>
              <div data-id="element-303">
                <label htmlFor="metricField" className={labelCls} data-id="element-304">
                  Of Field
                </label>
                <select id="metricField" value={config.metricField ?? ''} onChange={e => update({
              metricField: e.target.value || null
            })} disabled={config.metricFn === 'count'} className={`${selectCls} disabled:bg-navy-50 disabled:text-navy-300`} data-id="element-305">
                  {config.metricFn === 'count' ? <option value="" data-id="element-306">Records</option> : numericFields.map(f => <option key={f.key} value={f.key} data-id="element-307">
                        {f.label}
                      </option>)}
                </select>
              </div>
            </div>}

          <fieldset data-id="element-308">
            <legend className={labelCls} data-id="element-309">Visualization</legend>
            <div className="mt-2 grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Visualization type" data-id="element-310">
              {chartOptions.map(opt => {
              const disabled = opt.value !== 'table' && !config.groupBy;
              return <button key={opt.value} type="button" role="radio" aria-checked={config.chartType === opt.value} disabled={disabled} title={disabled ? 'Set a Group By to enable charts' : opt.label} onClick={() => update({
                chartType: opt.value
              })} className={`flex flex-col items-center gap-1 rounded-xl border py-2.5 text-[11px] font-medium transition-all ${config.chartType === opt.value ? 'border-navy-900 bg-navy-900 text-white' : disabled ? 'cursor-not-allowed border-navy-50 text-navy-200' : 'border-navy-100 text-navy-500 hover:border-navy-300 hover:text-navy-900'}`} data-id="element-311">
                    <opt.icon className="h-4 w-4" data-id="element-312" />
                    {opt.label}
                  </button>;
            })}
            </div>
            {!config.groupBy && <p className="mt-1.5 text-[11px] text-navy-300" data-id="element-313">
                Charts require a Group By field.
              </p>}
          </fieldset>
        </div>
      </div>

      {/* Live preview */}
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto thin-scroll" data-id="element-314">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-315">
          <div data-id="element-316">
            <div className="flex items-center gap-2" data-id="element-317">
              <h3 className="text-sm font-semibold text-navy-900" data-id="element-318">
                Live Preview
              </h3>
              {activeJoin && <span className="inline-flex items-center gap-1 rounded-full bg-accent-50 px-2 py-0.5 text-[10px] font-medium text-accent-800" data-id="element-319">
                  <LinkIcon className="h-2.5 w-2.5" data-id="element-320" />
                  {dataset.name} + {activeJoin.name}
                </span>}
            </div>
            <p className="text-xs text-navy-400" data-id="element-321">
              {result ? config.groupBy && result.grouped ? `${result.grouped.length} groups · ${result.rows.length} underlying records` : `${result.rows.length} records` : '—'}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-322">
            <button type="button" onClick={() => setSaveOpen(true)} className="card-float inline-flex h-9 items-center gap-2 rounded-full bg-white px-4 text-sm font-medium text-navy-800 transition-shadow hover:card-float-lg" data-id="element-323">
              <BookmarkIcon className="h-4 w-4 text-gold-500" data-id="element-324" />
              {loadedTemplate ? 'Update Template' : 'Save as Template'}
            </button>
            <ExportButton reportName={reportName} rowCount={result?.rows.length ?? 0} data-id="element-325" />
          </div>
        </div>

        <motion.div key={JSON.stringify(config)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.18
      }} className="card-float overflow-hidden rounded-2xl bg-white" data-id="element-326">
          {result && showChart ? <div className="p-4" data-id="element-327">
              <ReportChart data={result.grouped!} chartType={config.chartType} metricLabel={result.metricLabel} data-id="element-328" />
            </div> : result && config.groupBy && result.grouped ? <ReportTable columns={[{
          key: 'group',
          label: fields.find(f => f.key === config.groupBy)?.label ?? 'Group'
        }, {
          key: 'value',
          label: result.metricLabel
        }]} rows={result.grouped.map(g => ({
          group: g.group,
          value: g.value
        }))} data-id="element-329" /> : result && config.columns.length > 0 ? <ReportTable columns={result.columns} rows={result.rows} data-id="element-330" /> : <div className="flex h-48 items-center justify-center text-sm text-navy-400" data-id="element-331">
              Select at least one column to preview data.
            </div>}
        </motion.div>
      </div>

      <SaveTemplateDialog open={saveOpen} onClose={() => setSaveOpen(false)} config={config} existingTemplate={loadedTemplate} onSaved={id => {
      setSaveOpen(false);
      setSearchParams({
        template: id
      });
    }} saveTemplate={saveTemplate} data-id="element-332" />
    </div>;
}